import type { Slot } from '../api/types'
import { U_ZONI, danasZone, datumZone, pomakniDan, satZone, trenutakIli, trenutakZone } from './zona'

/**
 * MOTOR TERMINA — klijentska strana (Z22b §6). Čist račun, bez Reacta.
 *
 * Ono što kalendar, mreža slotova i potvrda moraju čitati JEDNAKO stoji ovdje,
 * jednom: koji su dani ponuđeni, kako se piše sat, kad se pokazuje "još N
 * mjesta" i kako se čita raspon. Da svaki ekran računa sam, prva razlika bila bi
 * "10:00" u mreži i "11:00" na potvrdi — isti podatak, dva sata (`lib/ponuda.ts`
 * i `lib/relacija.ts` prije njega).
 *
 * **VRIJEME JE SAT RESORTA** (D14-B §6, `lib/zona.ts`). Do ovog kruga je bilo
 * UTC — i to bi bilo dosljedno da je i UPIS bio UTC. Nije: obrazac je trenutak
 * slagao iz zone PREGLEDNIKA, pa je gost koji je tražio 18:30 na potvrdi čitao
 * 16:30. Ista brojka, dva sata, i to točno ljetni pomak.
 *
 * Sada oba smjera idu kroz jedan modul: raspored je zidni sat resorta, trenutak
 * na žici je UTC, a pretvorba postoji samo na jednom mjestu. Kad
 * `tenant.timezone` stigne (CLAUDE.md §18), mijenja se `lib/zona.ts`, a ne ovaj
 * modul i nijedan ekran.
 */

/** Koliko dana kalendar nudi u jednoj traci; dalje se lista. */
export const DANA_U_TRACI = 14

/**
 * Prag DISKRETNOG prikaza (Z22b §6): "još N mjesta" piše tek kad je slobodno
 * manje od trećine posude.
 *
 * Nije ukras nego pristojnost: "još 9 od 10" gostu ne znači ništa i djeluje kao
 * pritisak; "još 2" je podatak zbog kojeg će požuriti. Ekskluzivan resurs
 * (kapacitet 1) ne pokazuje ništa — ondje je "još 1" tautologija.
 */
export const PRAG_MJESTA = 0.3

/** `YYYY-MM-DD` po satu RESORTA — oblik koji poslužitelj prima kao datum. */
export function datumResorta(trenutak: Date): string {
  return datumZone(trenutak)
}

/** Datum pomaknut za N dana, po kalendaru resorta (`lib/zona.ts`). */
export function danUnaprijed(pomak: number, sada: Date = new Date()): string {
  return pomakniDan(danasZone(sada), pomak)
}

/**
 * Traka datuma: od danas, N dana unaprijed. Traka, a ne kalendar s mjesecima —
 * termin se bira "danas, sutra, u petak", a ne "17. rujna 2027".
 */
export function traka(dana: number = DANA_U_TRACI, sada: Date = new Date()): string[] {
  return Array.from({ length: dana }, (_, redni) => danUnaprijed(redni, sada))
}

/** Sat termina, `HH:mm` po satu resorta (vidi opis modula). */
export function satTermina(iso: string): string {
  const trenutak = trenutakIli(iso)

  return trenutak ? satZone(trenutak) : ''
}

/**
 * DATUM + VRIJEME IZ OBRASCA → trenutak u ISO obliku (Z22c §A/§B).
 *
 * Polja su ZIDNI SAT RESORTA (D14-B §6), a ne sat preglednika: gost bira 18:30
 * na satu recepcije, a ne na satu svog mobitela — koji je u zrakoplovu ostao na
 * krivoj zoni. Poslužitelj računa u UTC-u, pa se pretvorba događa OVDJE, na
 * jednom mjestu (`lib/zona.ts`), a ne u dva obrasca koja bi se prvom promjenom
 * razišla. Nepotpun ili neispravan unos vraća `null`: gumb se tada ne nudi,
 * umjesto da se pošalje "Invalid Date".
 */
export function trenutakIzPolja(datum: string, vrijeme: string): string | null {
  if (!datum || !vrijeme) return null

  return trenutakZone(datum, vrijeme)?.toISOString() ?? null
}

/**
 * TRENUTAK → POLJA OBRASCA, obrnuti smjer od {@link trenutakIzPolja}
 * (dotjerivanje-4 §2).
 *
 * Protuprijedlog nije novi termin nego POMAK gostovog: "ne može u 20, može u
 * 21". Zato obrazac ne kreće od praznog polja nego od onoga što je gost tražio —
 * pružatelj pomiče sat, a ne prepisuje datum. Polja su zidni sat resorta, kao i
 * pri slanju, pa pretvorba mora biti ista i u ovom smjeru; da svaki ekran računa
 * sam, prvi gost preko ponoći dobio bi prijedlog za krivi dan.
 *
 * Neispravan zapis daje prazna polja — obrazac tada izgleda kao da ničega nije
 * bilo, umjesto da ponudi "Invalid Date".
 */
export function poljaIzTrenutka(iso: string | null | undefined): { datum: string; vrijeme: string } {
  const trenutak = trenutakIli(iso)

  if (!trenutak) return { datum: '', vrijeme: '' }

  return { datum: datumZone(trenutak), vrijeme: satZone(trenutak) }
}

/**
 * Raspon "10:00 – 11:00" — ono što stoji na potvrdi i u "Mom danu".
 *
 * **Jednak kraj sažima se u JEDAN sat** (Z22c): upit i dostava su TRENUTAK
 * ("stol u 20 h"), a ne razdoblje — "20:00 – 20:00" bi tvrdio trajanje koje
 * nitko nije obećao. Isto pravilo kao relacija s istim krajevima, koja se sažima
 * u odredište (`lib/relacija.ts`).
 */
export function rasponTermina(pocetak: string, kraj: string): string {
  const od = satTermina(pocetak)
  const do_ = satTermina(kraj)

  return od === do_ ? od : `${od} – ${do_}`
}

/**
 * DAN, KAKO GOD BIO ZAPISAN → trenutak. Traka pločica šalje `YYYY-MM-DD`, a
 * kartica gosta cijeli ISO trenutak; oboje znači "koji je ovo dan po satu
 * resorta", pa oboje ulazi na ista vrata.
 *
 * Goli datum se čita kao PODNE u zoni, a ne kao ponoć: podne je jedini sat koji
 * nijedan ljetni pomak ne prebaci u susjedni dan.
 */
function danUTrenutak(datum: string): Date | null {
  return /^\d{4}-\d{2}-\d{2}$/.test(datum) ? trenutakZone(datum, '12:00') : trenutakIli(datum)
}

/**
 * Dan kao rečenica: "sri, 12.8." — kratko, jer stane u pločicu trake. Poredak i
 * imena dana daje `Intl`, pa nema ni tablice ni paketa; sve se čita po satu
 * resorta (vidi opis modula).
 */
export function danOznaka(datum: string, jezik: string): string {
  const trenutak = danUTrenutak(datum)

  if (!trenutak) return datum

  try {
    return new Intl.DateTimeFormat(jezik, {
      weekday: 'short',
      day: 'numeric',
      month: 'numeric',
      ...U_ZONI,
    }).format(trenutak)
  } catch {
    return datum
  }
}

/** Puni datum za potvrdu i detalj ("srijeda, 12. kolovoza 2026."). */
export function datumOznaka(iso: string, jezik: string): string {
  const trenutak = danUTrenutak(iso)

  if (!trenutak) return iso

  try {
    return new Intl.DateTimeFormat(jezik, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      ...U_ZONI,
    }).format(trenutak)
  } catch {
    return iso
  }
}

/**
 * KOMPAKTAN TERMIN — "sub 16.8. u 18:30" (D14-B §7).
 *
 * **Kartica nema mjesta za dvije rečenice o istoj stvari.** Dotad je gostov
 * popis rezervacija nosio raspon u jednom retku ("18:30 – 19:30") i puni datum u
 * drugom ("subota, 16. kolovoza 2026.") — pedeset znakova da se kaže kada, na
 * kartici koja cijela ima tri retka. Ovdje su dan i sat JEDNA rečenica, ista ona
 * kojom najava kaže kad se nešto sljedeće događa (`lib/sljedeciTermin.ts`).
 *
 * **Spojnica je RIJEČ, pa dolazi iz i18n-a** ("u", "at", "um"): datum i sat su
 * mjera i pišu se svugdje jednako, ali ono između njih je jezik.
 *
 * Neispravan zapis daje prazan niz — redak se tada ne crta, umjesto da na
 * kartici stoji "Invalid Date".
 */
export function terminKompaktno(
  t: (kljuc: string, opcije?: Record<string, unknown>) => string,
  iso: string | null | undefined,
  jezik: string,
): string {
  const trenutak = trenutakIli(iso)

  if (!trenutak) return ''

  return t('common.danSat', { dan: danOznaka(iso!, jezik), sat: satZone(trenutak) })
}

/**
 * Treba li uz termin pisati "još N mjesta".
 *
 * Ekskluzivan resurs (kapacitet 1) ne pokazuje ništa: ondje slobodno znači
 * "slobodno", a broj bi bio tautologija. Iznad praga se šuti — vidi
 * {@link PRAG_MJESTA}.
 */
export function pokaziMjesta(slot: Pick<Slot, 'slobodno' | 'kapacitet'>): boolean {
  return slot.kapacitet > 1 && slot.slobodno / slot.kapacitet < PRAG_MJESTA
}

/**
 * Stane li grupa u termin. Račun je isti onaj koji poslužitelj radi pri upisu
 * (`grupa_prevelika`), pa gumb ne vodi u 409 — ali ODLUKA ostaje poslužitelju:
 * ovo samo prigušuje pločicu koju gost ionako ne može uzeti.
 */
export function staneGrupa(slot: Pick<Slot, 'slobodno'>, osoba: number): boolean {
  return slot.slobodno >= osoba
}
